import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { ethers } from 'ethers';
import { useReadContracts } from './useContracts';
import type { MethaContracts } from '../lib/contracts';

export type ContractName = keyof MethaContracts;

/**
 * Generic typed wrapper around TanStack Query for contract reads.
 *
 * Domain hooks should narrow the return type T and the args list.
 * The query key is `['metha', contractName, fn, ...args]` so cache
 * invalidation after a tx works via queryClient.invalidateQueries({
 *   queryKey: ['metha', 'crowdsale']
 * }).
 *
 * @example
 *   const { data: period } = useContractQuery<bigint>(
 *     ['crowdsale', 'getCurrentPeriod'],
 *   );
 *
 *   const { data: balance } = useContractQuery<bigint>(
 *     ['token', 'balanceOf', address],
 *     { enabled: !!address },
 *   );
 */
export function useContractQuery<T = unknown>(
  key: readonly [ContractName, string, ...unknown[]],
  options?: Omit<UseQueryOptions<T, Error, T>, 'queryKey' | 'queryFn'>
) {
  const contracts = useReadContracts();
  const [contractName, fn, ...args] = key;

  return useQuery<T, Error, T>({
    queryKey: ['metha', ...key] as const,
    queryFn: async () => {
      const contract = contracts[contractName] as ethers.Contract;
      const method = contract[fn];
      if (typeof method !== 'function') {
        throw new Error(`Contract '${contractName}' has no function '${fn}'`);
      }
      const result = await method(...args);
      return result as T;
    },
    ...options,
  });
}
